import { getMockupsData } from '../api/analytics3d';

// Function to flatten nested objects and arrays
const flattenObject = (obj: any, prefix = ''): any => {
    const flattened: any = {};
    
    Object.keys(obj).forEach(key => {
        const value = obj[key];
        const newKey = prefix ? `${prefix}_${key}` : key;
        
        if (value === null || value === undefined) {
            flattened[newKey] = '';
        } else if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
            // Try to parse JSON strings
            try {
                const parsed = JSON.parse(value);
                if (typeof parsed === 'object') {
                    const nested = flattenObject(parsed, newKey);
                    Object.assign(flattened, nested);
                } else {
                    flattened[newKey] = value;
                }
            } catch (e) {
                // If parsing fails, keep as string
                flattened[newKey] = value;
            }
        } else if (Array.isArray(value)) {
            // For arrays, create a comma-separated string
            if (value.length === 0) {
                flattened[newKey] = '';
            } else if (typeof value[0] === 'object') {
                // If array contains objects, create multiple columns
                value.forEach((item, index) => {
                    const itemFlattened = flattenObject(item, `${newKey}_${index}`);
                    Object.assign(flattened, itemFlattened);
                });
            } else {
                // If array contains primitives, join them
                flattened[newKey] = value.join(', ');
            }
        } else if (typeof value === 'object') {
            // Recursively flatten nested objects
            const nested = flattenObject(value, newKey);
            Object.assign(flattened, nested);
        } else {
            flattened[newKey] = value;
        }
    });
    
    return flattened;
};

// Function to process tags into readable format
const processTags = (item: any): any => {
    if (!item.tags || !Array.isArray(item.tags)) {
        return item;
    }
    
    // Create separate columns for each tag category
    const tagsByCategory: Record<string, string[]> = {};
    
    item.tags.forEach((tag: any) => {
        const category = tag.category || 'other';
        if (!tagsByCategory[category]) {
            tagsByCategory[category] = [];
        }
        tagsByCategory[category].push(tag.tagName);
    });
    
    // Create new fields for each category
    const processed = { ...item };
    
    // Add readable tag columns
    processed.tags_objectCount = tagsByCategory.objectCount?.join(', ') || '';
    processed.tags_generalClass = tagsByCategory.generalClass?.join(', ') || '';
    processed.tags_objectsClasses = tagsByCategory.objectsClasses?.join(', ') || '';
    processed.tags_objectSpecific = tagsByCategory.objectSpecific?.join(', ') || '';
    
    // Also add all tags in one column for convenience
    processed.tags_all = item.tags.map((tag: any) => tag.tagName).join(', ');
    
    // Remove original tags array to avoid creating multiple columns
    delete processed.tags;
    
    return processed;
};

// Function to remove excluded fields from data
const removeExcludedFields = (data: any[]): any[] => {
    const excludedFields = [
        'mockupUrl', 
        'imageUrl', 
        'recognitionLogs',
        'additionalEvents',
        'tags_generalClass',
        'tags_objectsClasses',
        'tags_objectSpecific',
        'ip',
        'template',
        'product',
        'recognitionStatus'
    ];
    
    return data.map(item => {
        const cleaned = { ...item };
        excludedFields.forEach(field => {
            delete cleaned[field];
        });
        return cleaned;
    });
};

// Function to convert data to CSV
const convertToCSV = (data: any[]): string => {
    if (data.length === 0) return '';
    
    // Process tags first to create readable columns
    const processedData = data.map(item => processTags(item));
    
    // Remove excluded fields before flattening
    const cleanedData = removeExcludedFields(processedData);
    
    // Flatten all objects
    const flattenedData = cleanedData.map(item => flattenObject(item));
    
    // Get all unique keys from all objects
    const allKeys = new Set<string>();
    flattenedData.forEach(item => {
        Object.keys(item).forEach(key => allKeys.add(key));
    });
    
    const headers = Array.from(allKeys);
    
    // Create CSV header
    const csvHeader = headers.map(h => `"${h}"`).join(',');
    
    // Create CSV rows
    const csvRows = flattenedData.map(item => {
        return headers.map(header => {
            const value = item[header] !== undefined ? item[header] : '';
            // Escape quotes and wrap in quotes
            const stringValue = String(value).replace(/"/g, '""');
            return `"${stringValue}"`;
        }).join(',');
    });
    
    return [csvHeader, ...csvRows].join('\n');
};

// Function to download CSV file
const downloadCSV = (csvContent: string, filename: string) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

// Export state interface for resumable exports
export interface ExportState {
    allData: any[];
    currentPage: number;
    totalPages: number;
    totalCount: number;
    perPage: number;
    queryParams: any;
    csvChunks?: string[]; // Processed CSV chunks
    headerWritten?: boolean;
    currentDate?: string; // Current date being processed (YYYY-MM-DD)
    datesList?: string[]; // List of all dates to process
    currentDateIndex?: number; // Index in datesList
    recordsPerDay?: Record<string, number>; // Track records per day
}

// Helper function to generate array of dates between two dates
const getDatesInRange = (startDate: string, endDate: string): string[] => {
    const dates: string[] = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Ensure dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return [startDate]; // Fallback to single date
    }
    
    const currentDate = new Date(start);
    while (currentDate <= end) {
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        dates.push(`${year}-${month}-${day}`);
        currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates.length > 0 ? dates : [startDate];
};

// Helper function to download partial CSV
export const downloadPartialCSV = (csvChunks: string[], queryParams: any, currentPage: number, totalPages: number): void => {
    if (csvChunks.length === 0) {
        alert('No data to save');
        return;
    }
    
    const finalCSV = csvChunks.join('\n');
    const minDate = queryParams.minDate || 'start';
    const maxDate = queryParams.maxDate || 'end';
    const filename = `mockups_${minDate}_${maxDate}_partial_page${currentPage}of${totalPages}.csv`;
    
    const blob = new Blob([finalCSV], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    console.log(`Saved partial export: ${filename}`);
};

// Configuration for large exports
const CHUNK_SIZE = 10000; // Process 10K records at a time to save memory
const MEMORY_WARNING_THRESHOLD = 50000; // Show warning above 50K records

// Main export function that fetches all pages and exports to CSV as single file
// Processes data day-by-day for better reliability and memory management
// Supports resuming from a previous state if provided
// skipCurrentPage: if true, skips the page where error occurred
// skipCurrentDay: if true, skips the entire current day
export const exportMockupsToCSV = async (
    queryParams: any,
    onProgress?: (current: number, total: number, currentDate?: string, totalDates?: number) => void,
    resumeState?: ExportState,
    skipCurrentPage: boolean = false,
    skipCurrentDay: boolean = false
): Promise<void> => {
    let allData: any[] = [];
    let currentPage = 1;
    let totalPages = 1;
    let totalCount = 0;
    let perPage = 5000;
    let csvChunks: string[] = []; // Store CSV chunks as strings
    let headerWritten = false;
    let datesList: string[] = [];
    let currentDateIndex = 0;
    let recordsPerDay: Record<string, number> = {};
    
    try {
        // Generate list of dates to process
        const minDate = queryParams.minDate || queryParams.maxDate || new Date().toISOString().split('T')[0];
        const maxDate = queryParams.maxDate || minDate;
        
        // If resuming from previous state, restore everything
        if (resumeState) {
            allData = resumeState.allData || [];
            currentPage = resumeState.currentPage;
            totalPages = resumeState.totalPages;
            totalCount = resumeState.totalCount;
            perPage = resumeState.perPage;
            csvChunks = resumeState.csvChunks || [];
            headerWritten = resumeState.headerWritten || false;
            datesList = resumeState.datesList || getDatesInRange(minDate, maxDate);
            currentDateIndex = resumeState.currentDateIndex || 0;
            recordsPerDay = resumeState.recordsPerDay || {};
            
            const currentDate = datesList[currentDateIndex];
            console.log(`Resuming export from date ${currentDate} (day ${currentDateIndex + 1}/${datesList.length}), page ${currentPage} of ${totalPages}`);
            
            // If skipping current day, move to next day
            if (skipCurrentDay) {
                console.log(`Skipping day ${currentDate}`);
                currentDateIndex++;
                currentPage = 1;
                totalPages = 1;
                allData = [];
            }
            // If skipping current page, move to next page
            else if (skipCurrentPage) {
                console.log(`Skipping page ${currentPage + 1}`);
                currentPage = currentPage + 1;
                allData = []; // Clear any problematic data
            } else {
                // If we have data, process it first
                if (allData.length > 0) {
                    const csvPart = convertToCSV(allData);
                    const lines = csvPart.split('\n');
                    
                    if (!headerWritten) {
                        csvChunks.push(lines[0]); // Header
                        headerWritten = true;
                    }
                    if (lines.length > 1) {
                        csvChunks.push(lines.slice(1).join('\n')); // Data
                    }
                    allData = []; // Clear memory
                }
            }
        } else {
            // Generate list of dates from minDate to maxDate
            datesList = getDatesInRange(minDate, maxDate);
            currentDateIndex = 0;
            recordsPerDay = {};
            
            console.log(`Starting export for ${datesList.length} day(s) from ${minDate} to ${maxDate}`);
        }
        
        // Process each day
        for (let dateIdx = currentDateIndex; dateIdx < datesList.length; dateIdx++) {
            const currentDate = datesList[dateIdx];
            currentDateIndex = dateIdx;
            
            console.log(`Processing day ${dateIdx + 1}/${datesList.length}: ${currentDate}`);
            
            // Fetch first page of current day to get total pages for this day
            if (currentPage === 1 || dateIdx > (resumeState?.currentDateIndex || 0)) {
                const dayQueryParams = {
                    ...queryParams,
                    minDate: currentDate,
                    maxDate: currentDate
                };
                
                const firstResponse = await getMockupsData({
                    ...dayQueryParams,
                    page: 1,
                    limit: 5000
                });
                
                allData = firstResponse.result;
                totalCount = firstResponse.totalCount;
                perPage = firstResponse.perPage;
                totalPages = Math.ceil(totalCount / perPage);
                currentPage = 1;
                recordsPerDay[currentDate] = totalCount;
                
                console.log(`Day ${currentDate}: ${totalCount} records, ${totalPages} pages`);
                
                if (onProgress) {
                    onProgress(1, totalPages, currentDate, datesList.length);
                }
            }
            
            // Fetch remaining pages for current day
            const startPage = resumeState && dateIdx === resumeState.currentDateIndex ? currentPage + 1 : 2;
            for (let page = startPage; page <= totalPages; page++) {
                const dayQueryParams = {
                    ...queryParams,
                    minDate: currentDate,
                    maxDate: currentDate
                };
                
                const response = await getMockupsData({
                    ...dayQueryParams,
                    page,
                    limit: 5000
                });
                
                allData.push(...response.result);
                currentPage = page;
                
                if (onProgress) {
                    onProgress(page, totalPages, currentDate, datesList.length);
                }
                
                // Process chunk when we have enough data
                if (allData.length >= CHUNK_SIZE) {
                    const chunkToProcess = allData.splice(0, CHUNK_SIZE);
                    const csvPart = convertToCSV(chunkToProcess);
                    const lines = csvPart.split('\n');
                    
                    if (!headerWritten) {
                        csvChunks.push(lines[0]); // Header
                        headerWritten = true;
                        if (lines.length > 1) {
                            csvChunks.push(lines.slice(1).join('\n')); // Data rows
                        }
                    } else {
                        // Skip header, only add data rows
                        if (lines.length > 1) {
                            csvChunks.push(lines.slice(1).join('\n'));
                        }
                    }
                }
            }
            
            // Process remaining data from this day
            if (allData.length > 0) {
                const csvPart = convertToCSV(allData);
                const lines = csvPart.split('\n');
                
                if (!headerWritten) {
                    csvChunks.push(lines[0]); // Header
                    headerWritten = true;
                    if (lines.length > 1) {
                        csvChunks.push(lines.slice(1).join('\n'));
                    }
                } else {
                    if (lines.length > 1) {
                        csvChunks.push(lines.slice(1).join('\n'));
                    }
                }
                allData = []; // Clear for next day
            }
            
            // Reset for next day
            currentPage = 1;
            totalPages = 1;
        }
        
        // Join all chunks and download as single file
        const finalCSV = csvChunks.join('\n');
        const originalMinDate = queryParams.minDate || 'start';
        const originalMaxDate = queryParams.maxDate || 'end';
        const filename = `mockups_${originalMinDate}_${originalMaxDate}.csv`;
        
        // Use Blob for efficient memory handling
        const blob = new Blob([finalCSV], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        // Calculate total records
        const totalRecordsExported = Object.values(recordsPerDay).reduce((sum, count) => sum + count, 0);
        console.log(`Successfully exported ${totalRecordsExported} records from ${datesList.length} day(s) to ${filename}`);
        console.log('Records per day:', recordsPerDay);
        
    } catch (error) {
        console.error('Error exporting to CSV:', error);
        // Save current state for potential retry
        const errorState: ExportState = {
            allData,
            currentPage,
            totalPages,
            totalCount,
            perPage,
            queryParams,
            csvChunks,
            headerWritten,
            currentDate: datesList[currentDateIndex],
            datesList,
            currentDateIndex,
            recordsPerDay
        };
        // Attach state to error for component to handle
        (error as any).exportState = errorState;
        throw error;
    }
};

